import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { Project } from "../../../models/project";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { CachedUserService } from "../../../shared/services/cached-user.service";
import { RebuildTableComponent } from "../../project/dialogs/rebuild-project-table/rebuild-project-table.component";
import { ButtonConfig } from "../data-table/custom-table/custom-table.component";
import { parameter_view_fields } from "./fields";
import { FormControl, Validators } from "@angular/forms";
import { ProjectService } from "../../../shared/services/project.service";
import { header_tooltips } from "./header_tooltips";
import { take } from "rxjs/operators";
import { NzModalService } from "ng-zorro-antd/modal";
import { EditJobDialogComponent } from "../../project/dialogs/edit-job-dialog/edit-job-dialog.component";
import { NzNotificationService } from "ng-zorro-antd/notification";

interface JobItem {
  id: string;
  job__id: string;
  job__job_name: string;
  job__job_basepath: string;
}

interface GroupItem {
  id: string;
  name: string;
  project_name: string;
  created_by_name: string;
  comment: string;
  jobs: JobItem[];
}

@Component({
    selector: "app-parameter-overview",
    templateUrl: "./parameter-overview.component.html",
    styleUrls: ["./parameter-overview.component.less"],
    standalone: false
})
export class ParameterOverviewComponent implements OnInit, OnDestroy {
  // inject services
  private modalService = inject(NzModalService)
  private router = inject(Router)
  private cachedProjectService = inject(CachedProjectService)
  public dialog = inject(MatDialog)
  private userService = inject(CachedUserService)
  private _projectService = inject(ProjectService)
  private _route = inject(ActivatedRoute)
  private notificationService = inject(NzNotificationService)
  
  private _userSubscription: any;
  private _projectSubscription: Subscription;
  private _parameterTableSubscription: Subscription;

  headerTooltips: object = header_tooltips;
  isVisible: boolean = false;
  creating_group: boolean = false;
  nameControl: FormControl;
  commentControl: FormControl;
  jobsControl: FormControl;
  parentJobs: any[] = [];
  project_id: string;
  project_name: string;
  is_loading: boolean = false;
  data: Array<any> = [];
  options: Object = parameter_view_fields;
  linkDefinitions: Array<any> = [
    {
      key: "job_name",
      template: (row: object) => `projects/${row["job_id"]}/model`,
    },
    {
      key: "parent_job_name",
      template: (row: object) =>
        `projects/${row["parent_job_id"]}/model?iteration=${
          row["parent_job_cp"] || 0
        }`,
    },
  ];
  buttonConfig: ButtonConfig[] = [
    {
      text: "Open Showing Jobs",
      action: (event, info) => {
        window.open(
          `/projects/${info[0].job_id}/model?${info
            .map((job: Object) => `jobs=${job["job_id"]}`)
            .join("&")}`,
          "_blank"
        );
      },
    },
    {
      text: "Create Job Group",
      action: (event, info) => {
        console.log(
          info,
          info.map((p) => p.job_id)
        );
        this.jobsControl.setValue(info.map((p) => p.job_id));
        this.isVisible = true;
      },
    },
    {
      text: "Plot Tracefits",
      action: (event, info) => {
        window.open(
          `/project/${this.project_id}/trace-fits-plotting?${info
            .map((job: Object) => `jobs=${job["job_id"]}`)
            .join("&")}`,
          "_blank"
        );
      },
    },
  ];
  inMenuButtonConfig: ButtonConfig[] = [
    {
      text: "Rebuild Table",
      action: (event, info) => {
        this.rebuildTable();
      },
    },
    {
      text: "Refresh Table",
      action: (event, info) => {
        this.refreshTable();
      },
    },
    {
      text: "Export as TSV",
      action: (event, info, fields) => {
        this._exportAsTSV(event, info, fields);
      },
    },
  ];
  landingJobs: object[] = []; // These are the jobs to be shown when landing on the page. Are gotten from the URL
  jobGroups: GroupItem[] = [];
  selectedGroup: GroupItem;

  constructor() {}

  ngOnInit() {
    this._userSubscription = this.userService.userDetail.subscribe((d) => {
      // if (d && !(d.isStaff || d.email === "testuser01@s-cube.com"))
      //   console.log("this is whats happening");
      // this.router.navigateByUrl("/");
    });
    this._projectSubscription =
      this.cachedProjectService.currentProject.subscribe((project: Project) => {
        if (project != null) {
          this.project_id = project.id;
          this.project_name = project.name;
          this.nameControl = new FormControl("", Validators.required);
          this.commentControl = new FormControl("");
          this.jobsControl = new FormControl([], Validators.required);
          this.getJobGroups();
          this.get_parameter_table();
          this.getParentJobs();
        }
      });
  }

  ngOnDestroy(): void {
    this._parameterTableSubscription.unsubscribe();
    this._userSubscription.unsubscribe();
    this._projectSubscription.unsubscribe();
  }

  getParentJobs() {
    this.creating_group = true;
    this.cachedProjectService
      ._getProjectJobs(this.project_id, {
        fields: ["id", "job_name"],
      })
      .toPromise()
      .then((res) => {
        this.parentJobs = res.data;
      })
      .catch((err) => console.log(err))
      .finally(() => (this.creating_group = false));
  }

  async get_parameter_table(
    rebuild: boolean = false,
    forceRefresh: boolean = false
  ) {
    this.is_loading = true;
    let param_jobs = this._route.snapshot.queryParams.jobs || [];

    let group_id = this._route.snapshot.queryParams?.group || null;
    if (group_id) {
      let groups = await this.getJobGroups_async();

      let foundGroup = groups.find((g) => g.id === group_id);
      if (foundGroup) {
        param_jobs = foundGroup.jobs.map((job) => job.job__id);
        this.selectedGroup = foundGroup;
      }
    }

    // console.log(this._route.snapshot.queryParams);
    this._parameterTableSubscription = this.cachedProjectService
      .getParameterTable(this.project_id, rebuild, param_jobs, forceRefresh)
      .subscribe(
        (data) => {
          this.landingJobs = this._route.snapshot.queryParams.jobs;
          this.data = [...data["results"]];
          this.is_loading = false;
        },
        (err) => {
          console.log(err);
          this.is_loading = false;
        }
      );
  }

  onParameterAction = (event: Event, child: string) => {
    event.stopPropagation();
    this.cachedProjectService.setSelectedGlossaryParameter(child);
  }

  refreshTable() {
    this.get_parameter_table(false, true);
  }

  rebuildTable() {
    this.dialog
      .open(RebuildTableComponent, {
        height: "320px",
        width: "400px",
        data: {
          project_name: this.project_name,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result != undefined && result["confirm"]) {
          this.get_parameter_table(true);
        }
      });
  }

  createGroup() {
    // For form validation and showing errors
    if (!this.nameControl.value || this.nameControl.value == "") {
      this.nameControl.setErrors({ required: true });
    } else if (!this.jobsControl.value || this.jobsControl.value.length == 0) {
      this.jobsControl.setErrors({ required: true });
    } else {
      // if all is well
      this.creating_group = true;
      this._projectService
        .createJobGroups({
          project_id: this.project_id,
          name: this.nameControl.value,
          comment: this.commentControl.value,
          jobs: this.jobsControl.value,
        })
        .toPromise()
        .then((res) => {
          this.notificationService.success("Successfully created job group", null);
          this.resetFields();
        })
        .catch((err) => {
          console.log(err);
          this.notificationService.error('Error creating job group', err.message);
        })
        .finally(() => (this.creating_group = false));
    }
  }
  resetFields() {
    this.commentControl.reset();
    this.nameControl.reset();
    this.jobsControl.reset();
    this.isVisible = false;
  }

  _exportAsTSV(event, data, fields) {
    // its not csv, its tsv
    let csvstr = fields.map((f) => f["key"]).join("\t") + "\n";
    for (let row of data) {
      let entry = fields.map((x) => row[x["key"]]);
      csvstr += entry.join("\t") + "\n";
    }
    let blob = new Blob([csvstr], { type: "text/tab-separated-values" });
    let url = window.URL.createObjectURL(blob);
    let a = document.createElement("a");
    a.href = url;
    a.download = "table.tsv";
    a.click();
  }

  // -------------------------------------------- Miscellaneous --------------------------------------------
  showAllJobs() {
    this.selectedGroup = null;
    this.router
      .navigate([], {
        relativeTo: this._route,
        queryParams: {},
      })
      .then(() => this.get_parameter_table());
  }

  async getJobGroups_async() {
    const res = await this._projectService
      .getJobGroups(this.project_id)
      .toPromise();
    return res.data;
  }

  getJobGroups() {
    this._projectService
      .getJobGroups(this.project_id)
      .toPromise()
      .then((res) => {
        this.jobGroups = res.data;
      })
      .catch((err) => this.notificationService.error('Error getting job groups', err.message));
  }

  onGroupClick(group: GroupItem) {
    if (this.selectedGroup?.id != group.id) {
      this.selectedGroup = group;
      this.router
        .navigate([], {
          relativeTo: this._route,
          queryParams: { jobs: group.jobs.map((job) => job.job__id) },
          queryParamsHandling: "merge",
        })
        .then(() => this.get_parameter_table());
    }
  }

  isGroupSelected(group: GroupItem) {
    return this.selectedGroup?.id == group.id;
  }

  editJob(row: object){
    this.modalService
      .create({
        nzContent: EditJobDialogComponent,
        nzData: {
          jobId: row['job_id'],
        },
      })
      .afterClose.pipe(take(1))
      .subscribe((updated) => {
        if (!updated) {
          return;
        } else {
          this.get_parameter_table(false, true);
        }
      });
  }
}
